import { useEffect, useState } from "react";

// MUI
import { Box, Stack, SxProps } from "@mui/material";
import { GridColDef, GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";

// Components
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import TableCustomComponents from "@/DataPages/components/common/PaginationHeaderLoading";

// Helpers
import { generateSx, getPinnedCellClassName, getRowClassName } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { isFooterCell } from "@/Common/helpers/Tables";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Types
import { ITradeInPayOff } from "@/DataPages/types/financeManagement/model/trade-in-payoff";

// Constants
import { DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@/Washouts/constants";
import { COMBINATED_CLASSES as HEADER_CLASS_NAME, ONE_HUNDRED_THIRTY } from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

// Icons
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";

const pinnedColumns = { left: ["financeEmployeeName", "saleDate", "dealVin", "dealStockId", "dealNumber", "dealStatus", "__empty"] };

const renderCell = (params: GridRenderCellParams) =>
	isFooterCell(params) ? null : (
		<CustomCell
			value={params.value ?? ""}
			maxWith={params.colDef.maxWidth ?? params.colDef.width ?? ONE_HUNDRED_THIRTY}
			fontSize="12px"
		/>
	);

const renderCurrencyCell = (params: GridRenderCellParams) => (
	<TypographyCell render="absolute" params={params} hints={{ isMoney: true }} fontSize={isFooterCell(params) ? "14px" : "12px"} />
);

const renderDateCell = ({ value, colDef }: GridRenderCellParams) =>
	value ? (
		<CustomCell
			value={getFormatUTCDate(value, DATE_FORMAT.monthAbbrDatePadded)}
			maxWith={colDef.maxWidth ?? colDef.width ?? ONE_HUNDRED_THIRTY}
			fontSize="12px"
		/>
	) : null;

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		filterable: false,
	},
	{
		field: "financeEmployeeId",
		headerName: "Finance Employee Id",
	},
	{
		field: "financeEmployeeName",
		headerName: "Finance Employee",
		minWidth: ONE_HUNDRED_THIRTY,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (params) => getPinnedCellClassName(params),
		renderCell: (params) => (
			<TypographyCell render="absolute" params={params} fontSize={isFooterCell(params) ? "14px" : "12px"}>
				{isFooterCell(params) ? "Totals" : params.value}
			</TypographyCell>
		),
	},
	{
		field: "saleDate",
		headerName: "Sale Date",
		type: "date",
		width: 80,
		headerClassName: HEADER_CLASS_NAME,
		cellClassName: (param) => getPinnedCellClassName(param),
		valueGetter: (value) => value && getDateInUTC(value),
		valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded) ?? "",
		renderCell: (params) => (
			<TypographyCell params={params} fontSize={"12px"}>
				{getFormattedDate(params.value, "", DATE_FORMAT.monthAbbrDatePadded) ?? ""}
			</TypographyCell>
		),
	},
	{
		field: "dealVin",
		headerName: "VIN",
		minWidth: ONE_HUNDRED_THIRTY,
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCell,
	},
	{
		field: "dealStockId",
		headerName: "Stock #",
		minWidth: 70,
		width: 70,
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCell,
	},
	{
		field: "dealNumber",
		headerName: "Deal #",
		minWidth: 70,
		width: 70,
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCell,
	},
	{
		field: "dealStatus",
		headerName: "Status",
		minWidth: 90,
		width: 90,
		headerClassName: HEADER_CLASS_NAME,
		renderCell: (param) => {
			if (!param.value) {
				return null;
			}
			return (
				<Stack display="flex" direction="row" justifyContent="start">
					<Box>
						<CircleRoundedIcon
							className={`deal-status-${param.value}`}
							sx={{
								fontSize: "12px",
								marginTop: "-3px",
								marginRight: "5px",
							}}
						/>
					</Box>
					<CustomCell value={param.value} maxWith={param.colDef.width ?? 50} fontSize="12px" />
				</Stack>
			);
		},
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		minWidth: 12,
		width: 12,
		maxWidth: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	},
	{
		field: "actualCashValue",
		headerName: "Actual Cash Value",
		minWidth: ONE_HUNDRED_THIRTY,
		type: "number",
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCurrencyCell,
	},
	{
		field: "tradeInAmt",
		headerName: "Trade In Amt",
		minWidth: ONE_HUNDRED_THIRTY,
		type: "number",
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCurrencyCell,
	},
	{
		field: "overallowanceAmt",
		headerName: "Overallowance Amt",
		minWidth: ONE_HUNDRED_THIRTY,
		type: "number",
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCurrencyCell,
	},
	{
		field: "payoff",
		headerName: "Payoff",
		minWidth: ONE_HUNDRED_THIRTY,
		type: "number",
		headerClassName: HEADER_CLASS_NAME,
		renderCell: renderCurrencyCell,
	},
	{
		field: "vehicleYr",
		headerName: "Vehicle Year",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleYr,
		renderCell: renderCell,
	},
	{
		field: "vehicleMake",
		headerName: "Vehicle Make",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleMake,
		renderCell: renderCell,
	},
	{
		field: "makePfx",
		headerName: "Make Pfx",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.makePfx,
		renderCell: renderCell,
	},
	{
		field: "modelDesc",
		headerName: "Model Desc",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.modelDesc,
		renderCell: renderCell,
	},
	{
		field: "carline",
		headerName: "Carline",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.carline,
		renderCell: renderCell,
	},
	{
		field: "color",
		headerName: "Color",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.extClrDesc,
		renderCell: renderCell,
	},
	{
		field: "accentClr",
		headerName: "Accent Color",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.accentClr,
		renderCell: renderCell,
	},
	{
		field: "licExpDate",
		headerName: "Lic Exp Date",
		headerClassName: HEADER_CLASS_NAME,
		type: "date",
		valueGetter: (_, row: ITradeInPayOff) => {
			let licExpDate = row?.vehicle?.vehicleDetail?.licExpDate;
			return licExpDate ? new Date(licExpDate) : null;
		},
		renderCell: renderDateCell,
	},
	{
		field: "licNo",
		headerName: "Lic No",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.licNo,
		renderCell: renderCell,
	},
	{
		field: "noOfCyl",
		headerName: "No of Cyl",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.noOfCyl,
		renderCell: renderCell,
	},
	{
		field: "odomReading",
		headerName: "Odom Reading",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.odomReading,
		renderCell: renderCell,
	},
	{
		field: "newUsed",
		headerName: "New/Used",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.newUsed,
		renderCell: renderCell,
	},
	{
		field: "weight",
		headerName: "Weight",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.weight,
		renderCell: renderCell,
	},
	{
		field: "weightUnit",
		headerName: "Units of Measure",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.vehicle?.vehicleDetail?.unitsOfMeasure,
		renderCell: renderCell,
	},
	{
		field: "lenderName",
		headerName: "Lender Name",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.lenderName,
		renderCell: renderCell,
	},
	{
		field: "lenderType",
		headerName: "Lender Type",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.lenderType,
		renderCell: renderCell,
	},
	{
		field: "address",
		headerName: "Address",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.address?.addr1,
		renderCell: renderCell,
	},
	{
		field: "city",
		headerName: "City",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.address?.city,
		renderCell: renderCell,
	},
	{
		field: "state",
		headerName: "State",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.address?.state,
		renderCell: renderCell,
	},
	{
		field: "zip",
		headerName: "Zip",
		headerClassName: HEADER_CLASS_NAME,
		valueGetter: (_, row: ITradeInPayOff) => row?.lender?.address?.zip,
		renderCell: renderCell,
	},
];

interface ITradeInPayoffsTableProps {
	data: ITradeInPayOff[];
	isFetching: boolean;
	hasNextPage: boolean;
}

export default function TradeInPayoffsTable(props: ITradeInPayoffsTableProps) {
	const { data, isFetching, hasNextPage } = props;
	const apiRef = useGridApiRef();
	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => setSx(generateSx(apiRef)));
	}, [apiRef]);

	const initialState = {
		aggregation: {
			model: {
				actualCashValue: "sum",
				tradeInAmt: "sum",
				overallowanceAmt: "sum",
				payoff: "sum",
			},
		},
	};

	return (
		<DataSmartTable
			apiRef={apiRef}
			initialState={initialState}
			rows={data}
			getRowClassName={getRowClassName}
			columns={columns}
			pinnedColumns={pinnedColumns}
			getCellClassName={getPinnedCellClassName}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			rowHeight={DEFAULT_ROW_HEIGHT}
			autoHeight={false}
			maxRows={19}
			disableColumnMenu
			enableCustomScrollbars
			columnVisibilityModel={{ _id: false, financeEmployeeId: false }}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "mt-[8px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					filter: { hide: false },
					search: { useQuickFilter: true },
					customComponent: (
						<TableCustomComponents
							hasNextPage={hasNextPage}
							isFetching={isFetching}
							text={"Trade in payoffs data is still loading..."}
						/>
					),
				},
				noRowsOverlay: { title: "No trade in payoffs found" },
			}}
			customProps={{
				tableContainerClassName: "!h-full w-full [&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
			}}
			sx={sx}
		/>
	);
}
